// src/data/immigrationGuides/citizenshipCeremony.js

const citizenshipCeremony = {
    slug: "citizenship-ceremony",

    title: "British Citizenship Ceremony Guide: What Happens After Approval",

    // Matches the pattern used by the other guides. Without this, GuidePage
    // emits no BreadcrumbList JSON-LD for the page. "/immigration-guides" is
    // deliberately not used as the parent: it is not a real route, and pointing
    // a BreadcrumbList item at a 404 emits invalid structured data.
    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "Citizenship Ceremony",
            url: "/citizenship-ceremony"
        }
    ],

    seo: {
        metaTitle:
            "British Citizenship Ceremony UK: Oath & Certificate",
        metaDescription:
            "Learn what happens at a British citizenship ceremony, including oath, pledge, certificate collection and steps before applying for a passport.",
        keywords: [
            "British citizenship ceremony",
            "citizenship oath UK",
            "British citizenship certificate"
        ]
    },

    introduction: `
A [citizenship ceremony](https://www.gov.uk/citizenship-ceremonies) is the final
stage for many successful British citizenship applications.

Applicants become British citizens after completing the ceremony process and
receiving their certificate of citizenship.
`,

    sections: [
        {
            heading: "Booking your ceremony",
            content: `
You must attend a citizenship ceremony within 3 months of receiving your invitation
from the Home Office.

Booking your ceremony involves a few key points:

- Your local authority organises your citizenship ceremony, and you book it with them
- Ceremonies are usually done in groups, but you can ask for a private ceremony if
  you prefer, your local authority can tell you if there's an extra cost for this
- You must take your invitation with you to the ceremony, and you're usually
  allowed to take 2 guests
- If you become a British citizen under the Windrush scheme, you can choose
  whether to attend a ceremony, and you will not have to pay the fee
`
        },

        {
            heading: "How much it costs",
            content: `
The cost of your ceremony is included in your citizenship application fee, so you
do not pay separately for a standard group ceremony.

You might need to pay more for a private ceremony, check the cost with your local
authority.
`
        },

        {
            heading: "If you're not living in the UK",
            content: `
You can ask the embassy or consulate in the country you're living in whether you
can have your ceremony there instead.

If you're only abroad for a short time, you might be asked to postpone your
ceremony until you return to the UK. You must still book your ceremony within 3
months of getting your invitation.

If you'll be abroad for more than a few months, you might have to prove you're
planning to live in the UK permanently. If you applied for citizenship while in the
UK, you cannot have your ceremony abroad.
`
        },

        {
            heading: "What happens at a citizenship ceremony?",
            content: `
During the ceremony applicants make either:

- an oath of allegiance
- an affirmation of allegiance (if you prefer not to swear by God)

They also make a pledge to respect the rights, freedoms and laws of the UK.

At the end of the ceremony, you'll be presented with your certificate of British
citizenship and a welcome pack. Some local authorities sell photographs or videos
of the event.
`
        },

        {
            heading: "Citizenship certificate",
            content: `
After completing the ceremony, applicants receive a certificate of British
citizenship.

This certificate is important evidence of citizenship and is needed for a
[first British passport application](https://www.gov.uk/apply-first-adult-passport).
`
        },

        {
            heading: "When you do not need to attend a ceremony",
            content: `
You will not need to attend a citizenship ceremony if you're registering to become
a British overseas territories citizen, British overseas citizen, or British
subject.

You'll still need to make an oath or affirmation of allegiance, you'll be sent
details of how to do this.
`
        },

        {
            heading: "After the ceremony",
            content: `
After receiving the certificate, successful applicants can apply for a British
passport.
`
        }
    ],

    checklist: [
        "Receive ceremony invitation",
        "Attend ceremony",
        "Complete oath or affirmation",
        "Collect citizenship certificate",
        "Apply for passport"
    ],

    faqs: [
        {
            question: "When do I become British?",
            answer:
                "Applicants normally become British citizens after completing the citizenship ceremony process."
        },
        {
            question: "What happens during the oath?",
            answer:
                "Applicants make an oath or affirmation of allegiance and a pledge."
        },
        {
            question: "How long do I have to book my citizenship ceremony?",
            answer:
                "You must attend a citizenship ceremony within 3 months of receiving your invitation from the Home Office."
        },
        {
            question: "How much does a citizenship ceremony cost?",
            answer:
                "The cost is included in your citizenship application fee. A private ceremony may cost extra, check with your local authority."
        },
        {
            question: "Can I bring guests to my citizenship ceremony?",
            answer:
                "Yes, you're usually allowed to take 2 guests to your ceremony."
        },
        {
            question: "Can I have my citizenship ceremony abroad?",
            answer:
                "In some cases, yes: ask the embassy or consulate where you live. But if you applied for citizenship while in the UK, you cannot have your ceremony abroad."
        }
    ],

    relatedPages: [
        {
            title: "British Passport Application",
            url: "/british-passport-application"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK Citizenship Ceremonies",
            url: "https://www.gov.uk/citizenship-ceremonies"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-28"
    }
};

export default citizenshipCeremony;